import { useState, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import { fmtBRL, fmtDateShort, fmtNumber } from "../../lib/format";
import { countNoun, groupNouns, toCsv } from "../../lib/calculation-memory/memory-tabs";
import type { CalculationMemoryDetailRow, CalculationMemoryDto, CalculationMemoryGroupDetailsDto } from "@bi/types";
import { downloadText, fetchGroupDetails, MAX_PAGE } from "./memory-data";
import { PAGE_SIZE, Pager } from "./Pager";
import { IconDownload, IconSearch } from "../icons";

type Doc = CalculationMemoryDetailRow & { group: string };
type Col = {
  id: string;
  label: string;
  num?: boolean;
  sort: (d: Doc) => string | number;
  cell: (d: Doc) => ReactNode;
  csv: (d: Doc) => string | number | null;
  className?: string;
};

/** Grupos lidos por vez. Cada grupo é uma chamada à API (que só lista documentos por grupo). */
const STEP = 10;

/**
 * Lista de documentos que formam o número (CT-es, títulos, recebimentos…). A
 * API lista documentos por cliente/fornecedor, então a aba lê os maiores
 * grupos primeiro e diz quanto do total já está na lista; "Carregar mais"
 * busca os próximos.
 */
export function MemoryDocumentsTab({
  cardKey, unit, data, periodQS,
}: {
  cardKey: string;
  unit: "BRL" | "number";
  data: CalculationMemoryDto;
  periodQS: string;
}) {
  const nouns = groupNouns(data.breakdown.groupBy);
  const groups = data.breakdown.rows; // já do maior para o menor
  const [count, setCount] = useState(Math.min(STEP, groups.length));
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ id: string; dir: 1 | -1 }>({ id: "valor", dir: -1 });

  const loaded = groups.slice(0, count);
  const results = useQueries({
    queries: loaded.map((g) => ({
      queryKey: ["calculation-memory-docs", cardKey, g.groupKey, periodQS],
      queryFn: () => fetchGroupDetails(cardKey, g.groupKey, periodQS, MAX_PAGE, 0),
      staleTime: 5 * 60_000,
    })),
  });

  const pending = results.some((r) => r.isLoading);
  const failed = results.filter((r) => r.isError).length;
  const detailType = results.find((r) => r.data)?.data?.detailType ?? "movement";
  const truncated = results.filter((r) => r.data && r.data.total > r.data.rows.length).length;

  const docs: Doc[] = [];
  results.forEach((r, i) => {
    const d = r.data as CalculationMemoryGroupDetailsDto | undefined;
    if (!d) return;
    for (const row of d.rows) docs.push({ ...row, group: loaded[i]!.groupLabel });
  });


  const fmt = (v: number) => (unit === "number" ? fmtNumber(v) : fmtBRL(v));
  const cols = columnsFor(detailType, nouns.title, fmt, docs);

  const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const term = norm(q.trim());
  const filtered = term
    ? docs.filter((d) => norm([d.group, d.externalKey, d.statusName, d.plate ?? "", d.counterpartyName ?? ""].join(" ")).includes(term))
    : docs;
  const col = cols.find((c) => c.id === sort.id) ?? cols[cols.length - 1]!;
  const rows = [...filtered].sort((a, b) => {
    const x = col.sort(a), y = col.sort(b);
    return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "pt-BR")) * sort.dir;
  });

  const loadedValue = loaded.reduce((s, g) => s + g.total, 0);
  const totalValue = groups.reduce((s, g) => s + g.total, 0);
  const coverage = totalValue ? (loadedValue / totalValue) * 100 : 100;
  const allLoaded = count >= groups.length;

  function exportCsv() {
    downloadText(
      `memoria-${cardKey}-documentos.csv`,
      toCsv(cols.map((c) => c.label), rows.map((d) => cols.map((c) => c.csv(d)))),
    );
  }

  if (groups.length === 0) {
    return <div className="card p-6 text-sm text-gray-600">Nenhum documento neste período.</div>;
  }

  return (
    <section className="card" aria-labelledby="docs-titulo">
      <div className="px-5 py-3 border-b border-surface-border flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 id="docs-titulo" className="text-sm font-semibold text-gray-900">
            {fmtNumber(docs.length)} {countNoun(cardKey, docs.length)}
            {pending && <span className="font-normal text-gray-500"> (carregando…)</span>}
          </h3>
          <p className="text-xs text-gray-600 mt-0.5">
            {allLoaded
              ? `Todos os ${fmtNumber(groups.length)} ${nouns.many} do período.`
              : `Dos ${fmtNumber(count)} maiores ${nouns.many} de ${fmtNumber(groups.length)}, que somam ${coverage.toFixed(1).replace(".", ",")}% do total.`}
            {" "}Clique no título de uma coluna para ordenar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative">
            <span className="sr-only">Buscar documento</span>
            <IconSearch className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              className="input pl-8 py-1.5 text-sm w-72 max-w-full"
              placeholder={`Buscar ${nouns.one}, número ou placa`}
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
            />
          </label>
          <button type="button" className="btn-secondary text-xs py-1.5 flex items-center gap-1.5" onClick={exportCsv} disabled={!rows.length}>
            <IconDownload className="w-3.5 h-3.5" />
            Exportar CSV
          </button>
        </div>
      </div>

      {(failed > 0 || truncated > 0) && (
        <p className="px-5 py-2 text-xs text-warning-700 bg-warning-50 border-b border-surface-border">
          {failed > 0 && `Não foi possível ler os documentos de ${failed} ${failed === 1 ? nouns.one : nouns.many}. `}
          {truncated > 0 && `${truncated === 1 ? `Um ${nouns.one} tem` : `${truncated} ${nouns.many} têm`} mais de ${fmtNumber(MAX_PAGE)} documentos. Aqui aparecem os ${fmtNumber(MAX_PAGE)} primeiros de cada.`}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr className="border-b border-surface-border">
              {cols.map((c) => {
                const active = sort.id === c.id;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={active ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                    className={`px-3 py-2 font-medium whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}
                  >
                    <button
                      type="button"
                      onClick={() => { setPage(0); setSort({ id: c.id, dir: active ? (sort.dir === 1 ? -1 : 1) : c.num ? -1 : 1 }); }}
                      className={`inline-flex items-center gap-1 rounded hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${active ? "text-gray-900" : ""}`}
                    >
                      {c.label}
                      <span aria-hidden="true" className={active ? "" : "opacity-0"}>{sort.dir === 1 ? "↑" : "↓"}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((d, i) => (
              <tr key={`${d.externalKey}-${i}`} className="hover:bg-gray-50">
                {cols.map((c) => (
                  <td key={c.id} className={`px-3 py-2 ${c.num ? "text-right tabular-nums" : ""} ${c.className ?? ""}`}>{c.cell(d)}</td>
                ))}
              </tr>
            ))}
            {!pending && rows.length === 0 && (
              <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-sm text-gray-500">
                {q ? `Nenhum documento encontrado para "${q}".` : "Nenhum documento detalhado para estes grupos."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={rows.length} onPage={setPage} noun={countNoun(cardKey, rows.length)} />

      {!allLoaded && (
        <div className="px-5 py-3 border-t border-surface-border flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
          <span>Faltam {fmtNumber(groups.length - count)} {nouns.many}, com {(100 - coverage).toFixed(1).replace(".", ",")}% do total.</span>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary text-xs py-1.5" disabled={pending} onClick={() => setCount((c) => Math.min(groups.length, c + STEP))}>
              Carregar mais {Math.min(STEP, groups.length - count)} {nouns.many}
            </button>
            {groups.length - count > STEP && groups.length <= 100 && (
              <button type="button" className="btn-ghost text-xs py-1.5" disabled={pending} onClick={() => setCount(groups.length)}>
                Carregar todos
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function columnsFor(type: CalculationMemoryGroupDetailsDto["detailType"], groupTitle: string, fmt: (v: number) => string, docs: Doc[]): Col[] {
  const group: Col = {
    id: "grupo", label: groupTitle, sort: (d) => d.group, csv: (d) => d.group,
    cell: (d) => <span className="block max-w-[16rem] truncate text-gray-900" title={d.group}>{d.group}</span>,
  };
  const date = (id: string, label: string, get: (d: Doc) => string): Col => ({
    id, label, sort: (d) => get(d) ?? "", csv: (d) => (get(d) ? fmtDateShort(get(d)) : null),
    cell: (d) => (get(d) ? fmtDateShort(get(d)) : "—"), className: "whitespace-nowrap text-gray-700",
  });
  const money = (id: string, label: string, get: (d: Doc) => number, strong = false): Col => ({
    id, label, num: true, sort: get, csv: get,
    cell: (d) => <span className={strong ? "font-semibold text-gray-900" : "text-gray-700"}>{fmt(get(d))}</span>,
  });

  if (type === "movement") {
    const hasPlate = docs.some((d) => d.plate);
    return [
      date("data", "Data", (d) => d.dueDate),
      { id: "doc", label: "Documento", sort: (d) => d.statusName, csv: (d) => d.statusName, cell: (d) => <span className="whitespace-nowrap text-gray-900">{d.statusName}</span> },
      group,
      ...(hasPlate ? [{ id: "placa", label: "Placa", sort: (d: Doc) => d.plate ?? "", csv: (d: Doc) => d.plate ?? null, cell: (d: Doc) => <span className="font-mono text-xs text-gray-700">{d.plate ?? "—"}</span> }] : []),
      money("valor", "Valor", (d) => d.openAmount, true),
    ];
  }
  if (type === "balance") {
    return [
      group,
      { id: "conta", label: "Ag. / conta", sort: (d) => d.statusName, csv: (d) => d.statusName, cell: (d) => d.statusName },
      money("valor", "Saldo", (d) => d.openAmount, true),
    ];
  }
  if (type === "projection") {
    return [
      { id: "titulo", label: "Título", sort: (d) => d.externalKey, csv: (d) => d.externalKey, cell: (d) => <span className="font-mono text-xs text-gray-600">{d.externalKey}</span> },
      { id: "tipo", label: "Tipo", sort: (d) => d.statusName, csv: (d) => d.statusName, cell: (d) => d.statusName },
      { id: "contraparte", label: "Contraparte", sort: (d) => d.counterpartyName ?? "", csv: (d) => d.counterpartyName ?? null, cell: (d) => <span className="block max-w-[16rem] truncate">{d.counterpartyName ?? "—"}</span> },
      date("venc", "Vencimento", (d) => d.dueDate),
      money("valor", "Valor", (d) => d.openAmount, true),
    ];
  }
  // open_item: títulos em aberto / vencidos / a vencer
  const hasFilial = docs.some((d) => d.counterpartyName);
  return [
    { id: "titulo", label: "Título", sort: (d) => d.externalKey, csv: (d) => d.externalKey, cell: (d) => <span className="font-mono text-xs text-gray-600 whitespace-nowrap">{d.externalKey}</span> },
    group,
    ...(hasFilial ? [{ id: "filial", label: "Filial", sort: (d: Doc) => d.counterpartyName ?? "", csv: (d: Doc) => d.counterpartyName ?? null, cell: (d: Doc) => <span className="block max-w-[14rem] truncate text-gray-700" title={d.counterpartyName ?? ""}>{d.counterpartyName ?? "—"}</span> }] : []),
    date("emissao", "Emissão", (d) => d.issueDate),
    date("venc", "Vencimento", (d) => d.dueDate),
    money("original", "Original", (d) => d.invoiceAmount),
    money("valor", "Saldo", (d) => d.openAmount, true),
    {
      id: "atraso", label: "Dias de atraso", num: true, sort: (d) => d.daysOverdue, csv: (d) => d.daysOverdue || null,
      cell: (d) => (d.daysOverdue > 0 ? <span className="font-medium text-danger-600">{fmtNumber(d.daysOverdue)}</span> : <span className="text-gray-400">—</span>),
    },
  ];
}
